import { useFlags } from 'launchdarkly-react-client-sdk'
import PropTypes from 'prop-types'
import { Fragment, useState } from 'react'
import { useHistory } from 'react-router-dom'

import agent from 'agent'
import HaltConfirmation from 'components/attacks/HaltConfirmation'
import BusyOverlay from 'components/common/BusyOverlay'
import Button from 'components/common/Button'
import CardTooltip from 'components/common/CardTooltip'
import OffCanvas from 'components/common/OffCanvas'
import Popover from 'components/common/Popover'
import PopoverMenu from 'components/common/PopoverMenu'
import Text from 'components/common/Text'
import TextButton from 'components/common/TextButton'
import Tooltip from 'components/common/Tooltip'
import { isScenarioUsableForGameDay } from 'components/gamedays/gameday-utils'
import Icon from 'components/Icon'
import ScheduleWrapper from 'components/scenarios/detail/ScheduleWrapper'
import RerunConfirmationModal from 'components/scenarios/RerunConfirmationModal'
import ShareScenarioModal from 'components/scenarios/ShareScenarioModal'
import UpdateScenarioArchiveStatusTextButton from 'components/scenarios/UpdateScenarioArchiveStatusTextButton'
import { hasBeenRun } from 'components/scenarios/utils'
import { arrayFromGraph } from 'components/scenarios/utils/graph'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import theme from 'theme'
import { messageFromResponseError } from 'utils/strings'

import ResultsForm from './ResultsForm'

export function EditScenarioButton({ scenario }) {
  const history = useHistory()
  return (
    <Fragment>
      <Button
        data-test-edit-button
        iconOnly
        secondary
        data-track-action="edit-scenario"
        data-track-category="scenario-details"
        data-track-on="click"
        disabled={scenario.state === 'ARCHIVED'}
        title="Edit Scenario"
        onClick={() => {
          history.push({
            pathname: '/scenarios/edit',
            state: {
              scenario,
              backtrack: `/scenarios/detail/${scenario.guid}`
            }
          })
        }}
      >
        <Icon css={{ fontSize: theme.fontSizes[3] }} icon="pencil" />
      </Button>
    </Fragment>
  )
}

EditScenarioButton.propTypes = {
  scenario: PropTypes.object,
  doFetch: PropTypes.func
}

export function ScheduleScenarioButton({ scenario, schedules, doFetch }) {
  const [isOffCanvasOpen, setIsOffCanvasOpen] = useState(false)
  const steps = arrayFromGraph(scenario.graph)
  const isDisabled =
    !hasBeenRun(scenario) || steps.length < 1 || scenario.state === 'ARCHIVED'
  return (
    <Fragment>
      <Button
        data-test-modify-schedule
        data-test-schedule-button
        iconOnly
        secondary
        data-track-action="schedule-scenario"
        data-track-category="scenario-details"
        data-track-on="click"
        disabled={isDisabled}
        title={
          isDisabled
            ? 'Add at least one step and run this Scenario once before you can schedule it'
            : 'Edit schedule'
        }
        onClick={() => setIsOffCanvasOpen(true)}
      >
        <Icon css={{ fontSize: theme.fontSizes[4] }} icon="calendar" />
      </Button>
      <OffCanvas
        open={isOffCanvasOpen}
        setIsOpen={setIsOffCanvasOpen}
        title="Schedule Scenario"
      >
        <ScheduleWrapper
          fetchScenario={doFetch}
          scenarioId={scenario.guid}
          schedule={schedules[0]}
          setScheduleOffCanvas={setIsOffCanvasOpen}
        />
      </OffCanvas>
    </Fragment>
  )
}

ScheduleScenarioButton.propTypes = {
  scenario: PropTypes.object,
  schedules: PropTypes.array,
  doFetch: PropTypes.func
}

export function ShareScenarioButton({ scenario }) {
  const { currentTeamId } = useAuth()
  const [isShareModalOpen, setShareModalOpen] = useState(false)
  const steps = arrayFromGraph(scenario.graph)
  const isDisabled = !hasBeenRun(scenario) || steps.length < 1

  return (
    <Fragment>
      <Button
        iconOnly
        secondary
        data-track-action="share-scenario"
        data-track-category="scenario-details"
        data-track-on="click"
        disabled={isDisabled}
        title={
          isDisabled
            ? 'Add at least one step and run this Scenario once before you can share it'
            : 'Share scenario'
        }
        onClick={() => setShareModalOpen(true)}
      >
        <Icon css={{ fontSize: theme.fontSizes[4] }} icon="share" />
      </Button>
      <ShareScenarioModal
        currentTeamId={currentTeamId}
        open={isShareModalOpen}
        scenarioId={scenario.guid}
        onCancel={() => setShareModalOpen(false)}
        onConfirm={() => setShareModalOpen(false)}
      />
    </Fragment>
  )
}

ShareScenarioButton.propTypes = {
  scenario: PropTypes.object
}

export function RevertScenarioButton({ scenario, run, ...rest }) {
  const history = useHistory()

  return (
    <Button
      data-test-revert-button
      secondary
      data-track-action="revert-scenario"
      data-track-category="scenario-details"
      data-track-on="click"
      onClick={() => {
        history.push({
          pathname: '/scenarios/edit',
          state: {
            scenario: {
              ...scenario,
              ...run
            },
            backtrack: `/scenarios/detail/${scenario.guid}/runs/${run.run_number}`
          }
        })
      }}
      {...rest}
    >
      Revert to this version
    </Button>
  )
}

RevertScenarioButton.propTypes = {
  scenario: PropTypes.object,
  run: PropTypes.object
}

const ButtonWrapper = ({ isDraftState, ...rest }) => (
  <Button
    data-test-run-button
    data-track-action={isDraftState ? 'run-scenario' : 'rerun-scenario'}
    data-track-category="scenario-details"
    data-track-on="click"
    {...rest}
  >
    {isDraftState ? 'Run Scenario' : 'Rerun Scenario'}
  </Button>
)

ButtonWrapper.propTypes = {
  isDraftState: PropTypes.bool
}

export function RunScenarioButton({ onRunScenario, scenario, doFetch }) {
  const { currentTeamId } = useAuth()
  const history = useHistory()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const steps = arrayFromGraph(scenario.graph)
  const isDraftState = scenario.state === 'DRAFT'
  const isArchiveState = scenario.state === 'ARCHIVED'
  const isRunnable = steps.length > 0

  if (isArchiveState) {
    return (
      <Popover alignPopover={Popover.ALIGN_BOTTOM}>
        {({ getTargetProps, getPopoverProps }) => (
          <div css={{ display: 'inline-block' }} {...getTargetProps()}>
            <ButtonWrapper
              disabled
              css={{ pointerEvents: 'none' }}
              isDraftState={isDraftState}
              title={null}
            />
            <CardTooltip
              displayArrow
              variation="dark"
              {...getPopoverProps({
                css: {
                  maxWidth: 250
                }
              })}
            >
              <Text as="p" color="white">
                Unarchive this Scenario to run it again
              </Text>
            </CardTooltip>
          </div>
        )}
      </Popover>
    )
  }

  const runScenarioDefault = async () => {
    let runResponse
    try {
      runResponse = await agent.post(
        `/scenarios/${scenario.guid}/runs?teamId=${currentTeamId}`,
        {}
      )
    } catch (err) {
      toast({
        type: 'error',
        text: messageFromResponseError(
          err,
          'There was an issue running this scenario. Please try again later.'
        )
      })
      setIsModalOpen(false)
      return
    }
    await doFetch()
    setIsModalOpen(false)

    // The runs tab will redirect to the latest run by default
    const runNumber = runResponse?.data ?? ''
    setTimeout(() => {
      history.push(`/scenarios/detail/${scenario.guid}/runs/${runNumber}`)
    }, 300)
  }

  return (
    <Fragment>
      <Tooltip
        contents={() => {
          return (
            <Text as="p" color="white">
              Add an attack or Golden Signal to run this scenario
            </Text>
          )
        }}
        tooltipEnabled={!isRunnable}
      >
        <ButtonWrapper
          css={{
            pointerEvents: isRunnable ? 'initial' : 'none'
          }}
          disabled={!isRunnable}
          isDraftState={isDraftState}
          onClick={() => setIsModalOpen(true)}
        />
      </Tooltip>
      <RerunConfirmationModal
        isDraftState={isDraftState}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onConfirm={() => {
          if (onRunScenario) {
            onRunScenario(scenario)
          } else {
            runScenarioDefault()
          }
        }}
      />
    </Fragment>
  )
}

RunScenarioButton.propTypes = {
  onRunScenario: PropTypes.func,
  scenario: PropTypes.object,
  runs: PropTypes.array,
  doFetch: PropTypes.func
}

export function ScenarioHaltConfirmationModal({
  haltConfirmationOpen,
  setHaltConfirmationOpen,
  setDisableButton,
  selectedRun,
  doFetch,
  scenarioId,
  title,
  isCompletedNotification,
  ...rest
}) {
  const [isHalting, setIsHalting] = useState(false)
  const { currentTeamId } = useAuth()
  const onConfirmRequest = async ({ reason } = {}) => {
    const body = !reason ? {} : { reason }
    setDisableButton(true)
    try {
      setIsHalting(true)
      await agent.post(
        `/scenarios/halt/${scenarioId}/runs/${selectedRun.run_number}?teamId=${currentTeamId}`,
        body
      )
      setIsHalting(false)
      setDisableButton(false)
      setHaltConfirmationOpen(false)
      toast({
        type: 'success',
        text: 'Scenario successfully halted'
      })
      await doFetch()
    } catch (err) {
      setDisableButton(false)
      setHaltConfirmationOpen(false)
      toast({
        type: 'error',
        text: messageFromResponseError(
          err,
          'There was an issue halting your scenario. Please try again later'
        )
      })
    }
  }

  return isHalting ? (
    <BusyOverlay shown={true} text="Halting..." />
  ) : (
    <HaltConfirmation
      hideFooter
      doFetch={doFetch}
      message={
        <ResultsForm
          isConfirmationModal
          currentTeamId={currentTeamId}
          doFetch={doFetch}
          footerOptions={{
            confirmText: isCompletedNotification ? 'Save' : 'Confirm',
            cancelText: 'Cancel',
            confirmButtonType: isCompletedNotification
              ? 'primary'
              : 'destructive',
            cancelButtonType: 'secondary'
          }}
          hideSave={true}
          isCompletedNotification={isCompletedNotification}
          runInfo={selectedRun}
          showStatusCheckPrompt={isCompletedNotification}
          onCancel={() => setHaltConfirmationOpen(false)}
          onConfirm={
            isCompletedNotification
              ? () => setHaltConfirmationOpen(false)
              : onConfirmRequest
          }
        />
      }
      open={haltConfirmationOpen}
      showConfirmationContent={false}
      title={title || 'Halt scenario'}
      trackedEvent={
        isCompletedNotification ? 'ScenarioCompleted' : 'SingleScenarioHalted'
      }
      trackingOptions={{
        scenarioId: scenarioId,
        runNumber: selectedRun.run_number
      }}
      type="scenario"
      onCancel={() => setHaltConfirmationOpen(false)}
      onConfirmRequest={onConfirmRequest}
      {...rest}
    />
  )
}

ScenarioHaltConfirmationModal.propTypes = {
  selectedRun: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  scenarioId: PropTypes.string,
  doFetch: PropTypes.func,
  haltConfirmationOpen: PropTypes.bool,
  setHaltConfirmationOpen: PropTypes.func,
  setDisableButton: PropTypes.func,
  title: PropTypes.node,
  isCompletedNotification: PropTypes.bool
}

export function HaltScenarioButton({ scenario, selectedRun, doFetch }) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isHaltButtonDisabled, setIsHaltButtonDisabled] = useState(false)

  return (
    <Fragment>
      <ScenarioHaltConfirmationModal
        doFetch={doFetch}
        haltConfirmationOpen={isModalOpen}
        scenarioId={scenario.guid}
        selectedRun={selectedRun}
        setDisableButton={setIsHaltButtonDisabled}
        setHaltConfirmationOpen={setIsModalOpen}
      />
      <Button
        data-test-halt-button
        destructive
        data-track-action="halt-scenario"
        data-track-category="scenario-details"
        data-track-on="click"
        disabled={isHaltButtonDisabled}
        onClick={() => setIsModalOpen(true)}
      >
        {isHaltButtonDisabled ? 'Halting...' : 'Halt Scenario'}
      </Button>
    </Fragment>
  )
}

HaltScenarioButton.propTypes = {
  selectedRun: PropTypes.object,
  scenario: PropTypes.object,
  doFetch: PropTypes.func
}

export const ScenarioHeaderButtonsPopover = ({ scenario, doFetch }) => {
  const history = useHistory()
  const { createGamedays: isCreateGameDaysEnabled } = useFlags()
  const canCreateGameDayFromScenario = isScenarioUsableForGameDay(scenario)

  return (
    <Popover
      openOnClick
      alignPopover={Popover.ALIGN_RIGHT}
      arrowSize={theme.space[4]}
      topBuffer={theme.space[7]}
      onClick={(e) => e.preventDefault()}
    >
      {({ getTargetProps, getPopoverProps }) => (
        <Fragment>
          <Icon
            data-test-scenario-popover-actions
            icon="solidEllipsisVertical"
            {...getTargetProps({
              css: {
                fontSize: theme.fontSizes[3],
                color: theme.colors.steel[0],
                cursor: 'pointer',

                '&:hover': {
                  color: theme.colors.steel[2]
                }
              }
            })}
          />
          <PopoverMenu getPopoverProps={getPopoverProps}>
            {isCreateGameDaysEnabled && canCreateGameDayFromScenario && (
              <TextButton
                data-test-create-gameday-button
                secondary
                data-track-action="create-gameday-from-scenario"
                data-track-category="scenario-details"
                data-track-on="click"
                onClick={(event) => {
                  event.preventDefault()
                  history.push({
                    pathname: '/gamedays/new',
                    state: {
                      scenarioId: scenario.guid
                    }
                  })
                }}
              >
                Create GameDay from Scenario
              </TextButton>
            )}
            <TextButton
              data-test-duplicate-button
              secondary
              data-track-action="duplicate-scenario"
              data-track-category="scenario-details"
              data-track-on="click"
              onClick={(event) => {
                event.preventDefault()
                history.push({
                  pathname: '/scenarios/new',
                  state: {
                    scenario
                  }
                })
              }}
            >
              Duplicate
            </TextButton>
            <UpdateScenarioArchiveStatusTextButton
              data-track-category="scenario-details"
              doFetch={doFetch}
              scenario={scenario}
            />
          </PopoverMenu>
        </Fragment>
      )}
    </Popover>
  )
}

ScenarioHeaderButtonsPopover.propTypes = {
  scenario: PropTypes.object,
  doFetch: PropTypes.func
}

export const ScenarioHeaderBaseActions = ({ scenario, schedules, doFetch }) => {
  const { hasTeamPrivilege, currentTeamId, RBAC } = useAuth()

  return (
    <Fragment>
      <EditScenarioButton doFetch={doFetch} scenario={scenario} />
      <ScheduleScenarioButton
        doFetch={doFetch}
        scenario={scenario}
        schedules={schedules}
      />
      {hasTeamPrivilege(currentTeamId, RBAC.privileges.team.SHARE_SCENARIO) && (
        <ShareScenarioButton scenario={scenario} />
      )}
      <RunScenarioButton doFetch={doFetch} scenario={scenario} />
    </Fragment>
  )
}
ScenarioHeaderBaseActions.propTypes = {
  scenario: PropTypes.object,
  schedules: PropTypes.array,
  doFetch: PropTypes.func
}
